/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React, { useState,useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { Redirect, Switch, Route, useLocation } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Layout } from "../_metronic/layout";
import BasePage from "./BasePage";
import { Logout, AuthPage } from "./modules/Auth";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import { GetCookies } from "../_metronic/_helpers/Cookies";
import axios from "axios";
import { LOGO_BASE_URL, NODE_API_URL } from "../env_config";
import { setCcmodeConsumerSession, setCcModeParameterPermissions, setCcmodePlanFeatures, setCcModeSetting, setCcModeSettingAllowCurrencyChange } from "../redux/commonRedux/common.actions";
import { SessionModalProvider } from "../contexts/SessionModalContext";
import { getLanguageValue, LanguageProvider } from "../contexts/LanguageContext";
import { makeStyles, Paper, Typography } from "@material-ui/core";
import { fetchAndCacheTranslations } from "../_metronic/_partials/utilities/remoteTranslations";

const useStyles = makeStyles((theme) => ({
  disclaimerStrip: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: (props) => props.bgColor || theme.palette.grey[900], 
    color: (props) => props.textColor || theme.palette.common.white,       
    padding: "10px",          
    textAlign: 'center',
    zIndex: theme.zIndex.snackbar,      
  },
  mainContent: {
    paddingBottom: '50px', 
  }
}));

export function Routes() {
    const location = useLocation();
    const dispatch = useDispatch();
    const c_id = useMemo(()=>localStorage.getItem("C_id"),[])
    const consumerSessionId = useMemo(()=>JSON.parse(localStorage.getItem("consumerSessionId")),[])
    const [theme, setTheme] = useState("loader");
    const [themeData, setThemeData] = useState([]);
    const [ruleData, setRuleData] = useState([]);
    const [naturalData, setNaturalData] = useState([]);
    const [naturalFancyData, setNaturalFancyData] = useState([]);
    const [labData, setLabData] = useState([]);
    const [labFancyData, setLabFancyData] = useState([]);
    const [user, setUser] = useState([]);
    const [translationsVersion, setTranslationsVersion] = useState(0);

    // Publish the fixed disclaimer strip's height as a CSS var so bottom-anchored
    // toasts/snackbars can sit above it instead of hiding under it.
    const disclaimerRef = useRef(null);
    useLayoutEffect(() => {
        const applyDisclaimerHeight = () => {
            const height = themeData?.disclaimer && disclaimerRef.current ? disclaimerRef.current.offsetHeight : 0;
            document.documentElement.style.setProperty("--disclaimer-height", `${height}px`);
        };
        applyDisclaimerHeight();
        window.addEventListener("resize", applyDisclaimerHeight);
        return () => window.removeEventListener("resize", applyDisclaimerHeight);
    }, [themeData?.disclaimer]);

    useEffect(() => {
        fetchAndCacheTranslations().then((fresh) => {
            if (fresh) setTranslationsVersion((v) => v + 1); // triggers recompute below
        });
    }, []);

    const [language, setLanguage] = React.useState("en");

    const selectedLanguage = useMemo(
        () => getLanguageValue(language),
        [language, translationsVersion] // <-- add this
    );

    const { isAuthorized } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.user != null,
        }),
        shallowEqual
    );
    const { planFeatures } = useSelector(
      (state) => state.CcmodePlanFeaturesReducer
    );
    const authState = useSelector((state) => state.auth);
    function mergeArrayOfObjects(arrayOfObjects) {
      if (!arrayOfObjects.length) return {};
      return arrayOfObjects.reduce((merged, currentObj) => mergeObjectsDynamic(merged, currentObj));
    }

    const classes = useStyles({ bgColor: themeData?.nav_bar_color || "#000000", textColor: themeData?.nav_bar_font || "#FFFFFF" });
    
    function mergeObjectsDynamic(obj1, obj2) {
      const merged = { ...obj1 };
      
      for (const key in obj2) {
        if (obj2.hasOwnProperty(key)) {
          const value1 = obj1[key];
          const value2 = obj2[key];
    
          // Handle comma-separated string values for all fields
          if (typeof value1 === 'string' && typeof value2 === 'string') {
            merged[key] = mergeLists(value1, value2);
          }
          // Handle null or empty string values
          else if (value1 === null || value1 === "") {
            merged[key] = value2;
          }
          else if (value2 === null || value2 === "") {
            merged[key] = value1;
          }
          // Handle number ranges (min and max values)
          else if (typeof value1 === 'number' && typeof value2 === 'number') {
            merged[key] = key.includes('min') ? Math.min(value1, value2) : Math.max(value1, value2);
          }
          // Default case: keep value1 unless it's empty or null
          else {
            merged[key] = value1 !== null && value1 !== "" ? value1 : value2;
          }
        }
      }
    
      return merged;
    }
    
    // Enhanced Helper Function to Merge Comma-Separated Lists
    function mergeLists(list1, list2) {
      // Split the strings into arrays, trim whitespace, and merge into a set to remove duplicates
      const items1 = list1 ? list1.split(',').map(item => item.trim()) : [];
      const items2 = list2 ? list2.split(',').map(item => item.trim()) : [];
      const set = new Set([...items1, ...items2]);
      return Array.from(set).sort().join(','); // Sort and join to ensure consistent output
    }

    useEffect(() => {
      if (isAuthorized){
        GetCookies("resData").then(response => {
          if(response){
            let userData = JSON.parse(response)
            setUser(JSON.parse(response));
            axios({
              method: "POST",
              url: `${NODE_API_URL}/fetchCCMOdeSetting`,
              headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
              },
              data: {
                CustomerId:userData.CustomerId
              }
            })
            .then(function(res) {
              if(res && res.data && res.data.length){
                if(res.data.message === "UnAuthorized"){
                  localStorage.clear();
                  window.location.reload(true)
                }

                const storedUser = localStorage.getItem('userData');
                const oldUserData = storedUser ? JSON.parse(storedUser) : {};

                const mergedOverride = { 
                  ...oldUserData, 
                  ...res.data[0] 
                };

                const newUserData = mergedOverride;
                localStorage.removeItem('userData');
                localStorage.setItem('userData', JSON.stringify(newUserData));
                
                localStorage.setItem("backgroundImg",res.data[0].background_image)
                localStorage.setItem("logo",res.data[0].Logo)
                localStorage.setItem("backgroundColor",res.data[0].background_color)
                localStorage.setItem("nav_bar_color",res.data[0].nav_bar_color)
                localStorage.setItem("nav_bar_font",res.data[0].nav_bar_font)

                setThemeData(res.data[0])
                setTheme(res.data[0].Theme)
                if(res.data[0] && res.data[0].AllowLogin.data[0] === 0){
                    localStorage.clear();
                    window.location.replace("/")
                }
                dispatch(setCcModeSettingAllowCurrencyChange(res.data[0].allowCurrencyChange))
                // console.log("here1");
                // console.log(res.data[0]);
              }
              else{
                localStorage.clear();
                window.location.replace("/")
              }
            })
            .catch((res) => {
                console.warn(res);
            });

            // for rules 

            axios({
              method: "POST",
              url: `${NODE_API_URL}/ccmode/fetchCCModerule`,
              // url: `https://api.staging.global.diamondsupload.com/INTL/fetchCCModeSetting`,
              headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
              },
              data: {
                user_id:userData.CustomerId
              }
            })
            .then(function(res) {
              if(res && res.data){
                if(res.data.message === "UnAuthorized"){
                  localStorage.clear();
                  window.location.reload(true)
                }
                setRuleData(res.data.data)
                let checkstatus = res.data.data.map(value => value.status).every(element => element === 0)
                if(checkstatus){
                  localStorage.clear();
                  window.location.replace("/")
                }
                // setTheme(res.data[0].Theme)
                // console.log(res.data.data);
                // console.log("here2");
                // const resNaturalData = res.data.data.find(element => element.naturaldiamond === 1)
                // const resNaturalfancyData = res.data.data.find(element => element.naturalfancydiamond === 1)
                // const resLabData = res.data.data.find(element => element.labdiamond === 1)
                // const resLabFancyData = res.data.data.find(element => element.labfancydiamond === 1)
                // function reorderArray(array1, array2) {
                //   return array1.filter(item => array2?.split(",").includes(item))?.toString() || null;
                // }
                function reorderArray(array1, array2) {
                    if (!array2) return null;
                    const items = array2.split(",");
                    return [
                        ...array1.filter(item => items.includes(item)),   // in array1's order
                        ...items.filter(item => !array1.includes(item)),  // array2-only, appended
                    ].toString() || null;
                }
                const shapeNames = [ "ROUND", "ROUND MODIFIED", "PEAR", "OVAL", "MARQUISE", "HEART", "RADIANT", "PRINCESS","EMERALD", "ASSCHER", "SQUARE RADIANT", "CUSHION BRILLIANT",  "CUSHION MODIFIED", "BAGUETTE", "EUROPEAN CUT",  "OLD MINER", "BRIOLETTE", "BULLETS","CADILLAC", "CALF","CIRCULAR BRILLIANT","CRISS","EPAULETTE","FLAME",  "FLANDERS", "HALF MOON", "HEXAGON",  "KITE", "LOZENGE", "OCTAGON", "PENTAGON", "ROSE", "SHIELD",  "SQUARE", "STAR",  "TAPERED BAGUETTE", "TAPERED BULLET", "TRAPEZOID",  "TRIANGLE", "TRILLIANT"];
                const Color = ["D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "OP", "QR", "ST", "UV", "WX", "YZ"]
                const Cut = ["8X", "ID", "EX", "VG", "GD", "FR", "PR"]
                const Clarity = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2", "SI3", "I1", "I2", "I3"]
                const Lab = ["GIA", "IGI", "HRD", "AGS","SGL","GCAL","NON"]
                const Polish = ["EX", "VG", "GD", "FR", "PR"]
                const Fluorescence = ["NON", "FNT", "VSLT", "SLT", "MED", "STG", "VST"]
                const Shade = ["NONE", "BROWN", "GREEN", "GREY", "BLACK", "PINK", "BLUE", "LIGHT BROWN", "LIGHT GREEN", "MIX TINGE", "UNKNOWN"]
                const Milky = ["NO MILKY", "LIGHT MILKY", "MILKY", "UNKNOWN"]
                const Eyeclean = ["YES", "NO", "UNKNOWN"]
                const FancyColor = ["YELLOW", "PINK", "BLUE", "RED", "GREEN", "PURPLE", "ORANGE", "VIOLET", "GREY", "BLACK", "BROWN", "CHAMPAGNE", "COGNAC", "CHAMELEON", "WHITE"]
                const FancyIntensity = ["FAINT", "VERY LIGHT", "LIGHT", "FANCY LIGHT", "FANCY", "FANCY DARK", "FANCY INTENSE", "FANCY VIVID", "FANCY DEEP"]
                const FancyOvertone = ["NONE", "YELLOW", "Yellowish", "Pink", "Pinkish", "Blue", "Blueish", "Red", "Reddish", "Green", "Greenish", "Purple", "Purplish", "Orange", "Orangy", "Violet", "Violetish", "Gray", "Grayish", "Black", "Brown", "BROWNISH", "Champagne", "Cognac", "Chameleon", "WHITE"]
                const Availibiltity = ["Guaranteed","Memo,Hold","Subject to Prior sale"]
                let naturalMerged = mergeArrayOfObjects(res.data.data.filter(element => element.naturaldiamond === 1 && element.status === 1));
                let naturalruleid = res.data.data.filter(element => element.naturaldiamond === 1 && element.status === 1).map(item => item.rule_id).join(',')
                let naturaldata = Object.keys(naturalMerged).length?[{...naturalMerged,rule_id:naturalruleid}].map(value => {
                  if(value["diamond_type"] === "N"){
                    value["cut"] = reorderArray(Cut,value.cut)
                    value["clarity"] = reorderArray(Clarity,value.clarity)
                    value["lab"] = reorderArray(Lab,value.lab)
                    value["symmetry"] = reorderArray(Polish,value.symmetry)
                    value["fluorescence"] = reorderArray(Fluorescence,value.fluorescence)
                    value["polish"] = reorderArray(Polish,value.polish)
                    value["milky"] = reorderArray(Milky,value.milky)
                    value["eyeclean"] = reorderArray(Eyeclean,value.eyeclean)
                    value["shade"] = reorderArray(Shade,value.shade)
                    value["availability"] = reorderArray(Availibiltity,value.availability)
                    if(value.naturaldiamond){
                      value["color"] = reorderArray(Color,value.color)
                    }
                    else{
                      value["diamondfancy_color"] = reorderArray(FancyColor,value.diamondfancy_color)
                      value["diamondfancy_intensity"] = reorderArray(FancyIntensity,value.diamondfancy_intensity)
                      value["diamondfancy_overtone"] = reorderArray(FancyOvertone,value.diamondfancy_overtone)
                    }
                }
                else{
                  value["lab_shape"] = reorderArray(shapeNames,value.lab_shape)
                  value["lab_cut"] = reorderArray(Cut,value.lab_cut)
                  value["lab_clarity"] = reorderArray(Clarity,value.lab_clarity)
                  value["lab_lab"] = reorderArray(Lab,value.lab_lab)
                  value["lab_symmetry"] = reorderArray(Polish,value.lab_symmetry)
                  value["lab_fluorescence"] = reorderArray(Fluorescence,value.lab_fluorescence)
                  value["lab_polish"] = reorderArray(Polish,value.lab_polish)
                  value["lab_milky"] = reorderArray(Milky,value.lab_milky)
                  value["lab_eyeclean"] = reorderArray(Eyeclean,value.lab_eyeclean)
                  value["lab_shade"] = reorderArray(Shade,value.lab_shade)
                    value["labavailability"] = reorderArray(Availibiltity,value.labavailability)
                  if(value.labdiamond){
                    value["lab_color"] = reorderArray(Color,value.lab_color)
                  }
                  else{
                    value["lab_fancy_color"] = reorderArray(FancyColor,value.lab_fancy_color)
                    value["lab_fancy_intensity"] = reorderArray(FancyIntensity,value.lab_fancy_intensity)
                    value["lab_fancy_overtone"] = reorderArray(FancyOvertone,value.lab_fancy_overtone)
                  }
                }
                  return {
                    ...value
                  }
                }):[]
                setNaturalData(naturaldata)

                let naturalFancyMerged = mergeArrayOfObjects(res.data.data.filter(element => element.naturalfancydiamond === 1 && element.status === 1));
                let naturalfancyruleid = res.data.data.filter(element => element.naturalfancydiamond === 1 && element.status === 1).map(item => item.rule_id).join(',')
                let naturalfancydata = Object.keys(naturalFancyMerged).length?[{...naturalFancyMerged,rule_id:naturalfancyruleid}].map(value => {
                  if(value["diamond_type"] === "N"){
                    value["shape"] = reorderArray(shapeNames,value.shape)
                    value["cut"] = reorderArray(Cut,value.cut)
                    value["clarity"] = reorderArray(Clarity,value.clarity)
                    value["lab"] = reorderArray(Lab,value.lab)
                    value["symmetry"] = reorderArray(Polish,value.symmetry)
                    value["fluorescence"] = reorderArray(Fluorescence,value.fluorescence)
                    value["polish"] = reorderArray(Polish,value.polish)
                    value["milky"] = reorderArray(Milky,value.milky)
                    value["eyeclean"] = reorderArray(Eyeclean,value.eyeclean)
                    value["shade"] = reorderArray(Shade,value.shade)
                    value["availability"] = reorderArray(Availibiltity,value.availability)
                    if(value.naturaldiamond){
                      value["color"] = reorderArray(Color,value.color)
                    }
                    else{
                      value["diamondfancy_color"] = reorderArray(FancyColor,value.diamondfancy_color)
                      value["diamondfancy_intensity"] = reorderArray(FancyIntensity,value.diamondfancy_intensity)
                      value["diamondfancy_overtone"] = reorderArray(FancyOvertone,value.diamondfancy_overtone)
                    }
                }
                else{
                  value["lab_shape"] = reorderArray(shapeNames,value.lab_shape)
                  value["lab_cut"] = reorderArray(Cut,value.lab_cut)
                  value["lab_clarity"] = reorderArray(Clarity,value.lab_clarity)
                  value["lab_lab"] = reorderArray(Lab,value.lab_lab)
                  value["lab_symmetry"] = reorderArray(Polish,value.lab_symmetry)
                  value["lab_fluorescence"] = reorderArray(Fluorescence,value.lab_fluorescence)
                  value["lab_polish"] = reorderArray(Polish,value.lab_polish)
                  value["lab_milky"] = reorderArray(Milky,value.lab_milky)
                  value["lab_eyeclean"] = reorderArray(Eyeclean,value.lab_eyeclean)
                  value["lab_shade"] = reorderArray(Shade,value.lab_shade)
                    value["labavailability"] = reorderArray(Availibiltity,value.labavailability)
                  if(value.labdiamond){
                    value["lab_color"] = reorderArray(Color,value.lab_color)
                  }
                  else{
                    value["lab_fancy_color"] = reorderArray(FancyColor,value.lab_fancy_color)
                    value["lab_fancy_intensity"] = reorderArray(FancyIntensity,value.lab_fancy_intensity)
                    value["lab_fancy_overtone"] = reorderArray(FancyOvertone,value.lab_fancy_overtone)
                  }
                }
                  return {
                    ...value
                  }
                }):[]
                setNaturalFancyData(naturalfancydata)

                let labgrownMerged = mergeArrayOfObjects(res.data.data.filter(element => element.labdiamond === 1 && element.status === 1));
                let labgrownruleid = res.data.data.filter(element => element.labdiamond === 1 && element.status === 1).map(item => item.rule_id).join(',')
                let labgrowndata = Object.keys(labgrownMerged).length?[{...labgrownMerged,rule_id:labgrownruleid}].map(value => {
                  if(value["diamond_type"] === "N"){
                    value["shape"] = reorderArray(shapeNames,value.shape)
                    value["cut"] = reorderArray(Cut,value.cut)
                    value["clarity"] = reorderArray(Clarity,value.clarity)
                    value["lab"] = reorderArray(Lab,value.lab)
                    value["symmetry"] = reorderArray(Polish,value.symmetry)
                    value["fluorescence"] = reorderArray(Fluorescence,value.fluorescence)
                    value["polish"] = reorderArray(Polish,value.polish)
                    value["milky"] = reorderArray(Milky,value.milky)
                    value["eyeclean"] = reorderArray(Eyeclean,value.eyeclean)
                    value["shade"] = reorderArray(Shade,value.shade)
                    value["availability"] = reorderArray(Availibiltity,value.availability)
                    if(value.naturaldiamond){
                      value["color"] = reorderArray(Color,value.color)
                    }
                    else{
                      value["diamondfancy_color"] = reorderArray(FancyColor,value.diamondfancy_color)
                      value["diamondfancy_intensity"] = reorderArray(FancyIntensity,value.diamondfancy_intensity)
                      value["diamondfancy_overtone"] = reorderArray(FancyOvertone,value.diamondfancy_overtone)
                    }
                }
                else{
                  value["lab_shape"] = reorderArray(shapeNames,value.lab_shape)
                  value["lab_cut"] = reorderArray(Cut,value.lab_cut)
                  value["lab_clarity"] = reorderArray(Clarity,value.lab_clarity)
                  value["lab_lab"] = reorderArray(Lab,value.lab_lab)
                  value["lab_symmetry"] = reorderArray(Polish,value.lab_symmetry)
                  value["lab_fluorescence"] = reorderArray(Fluorescence,value.lab_fluorescence)
                  value["lab_polish"] = reorderArray(Polish,value.lab_polish)
                  value["lab_milky"] = reorderArray(Milky,value.lab_milky)
                  value["lab_eyeclean"] = reorderArray(Eyeclean,value.lab_eyeclean)
                  value["lab_shade"] = reorderArray(Shade,value.lab_shade)
                    value["labavailability"] = reorderArray(Availibiltity,value.labavailability)
                  if(value.labdiamond){
                    value["lab_color"] = reorderArray(Color,value.lab_color)
                  }
                  else{
                    value["lab_fancy_color"] = reorderArray(FancyColor,value.lab_fancy_color)
                    value["lab_fancy_intensity"] = reorderArray(FancyIntensity,value.lab_fancy_intensity)
                    value["lab_fancy_overtone"] = reorderArray(FancyOvertone,value.lab_fancy_overtone)
                  }
                }
                  return {
                    ...value
                  }
                }):[]
                setLabData(labgrowndata)

                let labgrownFancyMerged = mergeArrayOfObjects(res.data.data.filter(element => element.labfancydiamond === 1 && element.status === 1));
                let labgrownfancyruleid = res.data.data.filter(element => element.labfancydiamond === 1 && element.status === 1).map(item => item.rule_id).join(',')
                let labgrownfancydata = Object.keys(labgrownFancyMerged).length?[{...labgrownFancyMerged,rule_id:labgrownfancyruleid}].map(value => {
                  if(value["diamond_type"] === "N"){
                    value["shape"] = reorderArray(shapeNames,value.shape)
                    value["cut"] = reorderArray(Cut,value.cut)
                    value["clarity"] = reorderArray(Clarity,value.clarity)
                    value["lab"] = reorderArray(Lab,value.lab)
                    value["symmetry"] = reorderArray(Polish,value.symmetry)
                    value["fluorescence"] = reorderArray(Fluorescence,value.fluorescence)
                    value["polish"] = reorderArray(Polish,value.polish)
                    value["milky"] = reorderArray(Milky,value.milky)
                    value["eyeclean"] = reorderArray(Eyeclean,value.eyeclean)
                    value["shade"] = reorderArray(Shade,value.shade)
                    value["availability"] = reorderArray(Availibiltity,value.availability)
                    if(value.naturaldiamond){
                      value["color"] = reorderArray(Color,value.color)
                    }
                    else{
                      value["diamondfancy_color"] = reorderArray(FancyColor,value.diamondfancy_color)
                      value["diamondfancy_intensity"] = reorderArray(FancyIntensity,value.diamondfancy_intensity)
                      value["diamondfancy_overtone"] = reorderArray(FancyOvertone,value.diamondfancy_overtone)
                    }
                }
                else{
                  value["lab_shape"] = reorderArray(shapeNames,value.lab_shape)
                  value["lab_cut"] = reorderArray(Cut,value.lab_cut)
                  value["lab_clarity"] = reorderArray(Clarity,value.lab_clarity)
                  value["lab_lab"] = reorderArray(Lab,value.lab_lab)
                  value["lab_symmetry"] = reorderArray(Polish,value.lab_symmetry)
                  value["lab_fluorescence"] = reorderArray(Fluorescence,value.lab_fluorescence)
                  value["lab_polish"] = reorderArray(Polish,value.lab_polish)
                  value["lab_milky"] = reorderArray(Milky,value.lab_milky)
                  value["lab_eyeclean"] = reorderArray(Eyeclean,value.lab_eyeclean)
                  value["lab_shade"] = reorderArray(Shade,value.lab_shade)
                    value["labavailability"] = reorderArray(Availibiltity,value.labavailability)
                  if(value.labdiamond){
                    value["lab_color"] = reorderArray(Color,value.lab_color)
                  }
                  else{
                    value["lab_fancy_color"] = reorderArray(FancyColor,value.lab_fancy_color)
                    value["lab_fancy_intensity"] = reorderArray(FancyIntensity,value.lab_fancy_intensity)
                    value["lab_fancy_overtone"] = reorderArray(FancyOvertone,value.lab_fancy_overtone)
                  }
                }
                  return {
                    ...value
                  }
                }):[]
                setLabFancyData(labgrownfancydata)
                
              }
            })
            .catch((res) => {
                console.warn(res);
            });
          }
          else{
            localStorage.clear();
            window.location.replace("/")
          }
        })
        GetCookies("language").then(response => {  
          if(response){
              setLanguage(response)
          }
      })
      axios({
        method: 'GET',
        url:"https://api.ipify.org",
    })
      .then(function (res) {
          localStorage.setItem("ipData",res.data)
      })
      .catch((err) => {
        
      });
      }
     
    }, [isAuthorized]);

    function fetchUserPermissions(userData, cancelToken){
      axios({
          method: "GET",
          url: `${NODE_API_URL}/ccmode/fetch-user-permissions/${userData.userId}`,
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authState.authToken}`
          },
          cancelToken: cancelToken,
        })
        .then(function(res) {
          if(res && res.data && res.data.success){
            if(res.data.message === "UnAuthorized"){
              localStorage.clear();
              window.location.reload(true)
          }

          const settings = {
            hideCertificateNumber: 0,
            hideEstimatedShippingDays: 0,
            hidePrice: 0,
            displaybuybtn: 0,
            displayNatural: 0,
            displayLab: 0,
            startSession: 0,
            cartNotification: 0,
            displayName: userData.displayName,
            allowCurrencyChange: 1,
            isBuyRequest: 0,
            enableShare: 1,
            shareViaText: 1,
            shareViaWhatsapp: 1,
            shareViaEmail: 1
          };
          
          res.data.data.forEach(permission => {
            if (permission.master_column) {
              if(permission.master_column === "hideCertificateNumber"){
                settings[permission.master_column] = permission.is_active ? 0 : 1;
              } else if(permission.master_column === "hideEstimatedShippingDays"){
                settings[permission.master_column] = permission.is_active ? 0 : 1;
              } else if(permission.master_column === "hidePrice"){
                settings[permission.master_column] = permission.is_active ? 0 : 1;
              } else if(permission.master_column === "displaybuybtn"){
                settings[permission.master_column] = permission.is_active;
              } else if(permission.master_column === "cartNotification"){
                settings[permission.master_column] = permission.is_active;
              } else if(permission.master_column === "enableShare"){
                settings[permission.master_column] = permission.is_active ? 1 : 0;
              } else if(permission.master_column === "shareViaText"){
                settings[permission.master_column] = permission.is_active ? 1 : 0;
              } else if(permission.master_column === "shareViaWhatsapp"){
                settings[permission.master_column] = permission.is_active ? 1 : 0;
              } else if(permission.master_column === "shareViaEmail"){
                settings[permission.master_column] = permission.is_active ? 1 : 0;
              }
            } else {
              if (permission.permission_name === "Display Natural") {
                settings.displayNatural = permission.is_active;
              } else if (permission.permission_name === "Display Lab") {
                settings.displayLab = permission.is_active;
              } else if (permission.permission_name === "Start Session") {
                settings.startSession = permission.is_active;
              } else if(permission.permission_name === "isBuyRequest") {
                settings.isBuyRequest = permission.is_active;
              }
            }
          });
            
          dispatch(setCcModeSetting(settings));
        }
        else{
            localStorage.clear();
            window.location.replace("/")
          }
        })
        .catch((err) => {
          console.warn(err);
        });
    }

    function fetchParameterPermissions(cancelToken){
      axios({
        method: "GET",
        url: `${NODE_API_URL}/ccmode/fetch-parameter-permissions`,
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authState.authToken}`
        },
        cancelToken: cancelToken,
      })
      .then(function(res) {
        if(res && res.data && res.data.success){
          if(res.data.message === "UnAuthorized"){
            localStorage.clear();
            window.location.reload(true)
          }
          dispatch(setCcModeParameterPermissions(res.data.data));
        }
        else{
          localStorage.clear();
          window.location.replace("/")
        }
      })
      .catch((err) => {
        console.warn(err);
      });
    }

    function fetchConsumerSession(consumerId, cancelToken){
      axios({
        method: "GET",
        url: `${NODE_API_URL}/ccmode/ccmode-get-session/${consumerId}`,
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authState.authToken}`
        },
        cancelToken: cancelToken,
      })
      .then(function(res) {
        if(res && res.data && res.data.success){
          if(res.data.message === "UnAuthorized"){
            localStorage.clear();
            window.location.reload(true)
          }
          dispatch(setCcmodeConsumerSession(res.data.data))
        }
        else{
          localStorage.clear();
          window.location.replace("/")
        }
      })
      .catch((res) => {
        console.warn(res);
      });
    }

    function fetchPlanFeatures(cancelToken){
      axios({
        method: "GET",
        url: `${NODE_API_URL}/ccmode/fetch-plan-features`,
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authState.authToken}`
        },
        cancelToken: cancelToken,
      })
      .then(function(res) {
        if(res && res.data && res.data.success){
          if(res.data.message === "UnAuthorized"){
            localStorage.clear();
            window.location.reload(true)
          }
          dispatch(setCcmodePlanFeatures(res.data.data))
        }
        else{
          localStorage.clear();
          window.location.replace("/")
        }
      })
      .catch((res) => {
        console.warn(res);
      });
    }

    useEffect(()=>{
      const source = axios.CancelToken.source();
      
      if (isAuthorized){
        const userData = JSON.parse(authState.user);
        fetchUserPermissions(userData, source.token)
        fetchParameterPermissions(source.token)
        if(c_id && c_id > 0 && consumerSessionId && consumerSessionId > 0){
          fetchConsumerSession(consumerSessionId, source.token)
        } else {
          dispatch(setCcmodeConsumerSession(null))
        }
        fetchPlanFeatures(source.token)

        if(planFeatures){

          if(planFeatures["background_img"].allowed === 0){
            localStorage.removeItem("backgroundImg")
          } else{
            localStorage.setItem("backgroundImg",userData?.background_image)
          }
          
          if(planFeatures["logo"].allowed === 0){
            localStorage.removeItem("logo")
          } else{
            localStorage.setItem("logo",userData?.Logo)
          }

          localStorage.setItem("backgroundColor",userData?.background_color)
          localStorage.setItem("nav_bar_color",userData?.nav_bar_color)
          localStorage.setItem("nav_bar_font",userData?.nav_bar_font)
        }
      }

      return () => {
        source.cancel("Component unmounted: cancel pending request");
      };
    },[location])

    if(isAuthorized && themeData.length === 0) return null;

    return (
        <LanguageProvider value={selectedLanguage}>
          {(user && themeData && ruleData) &&
            <style>
              {`

                
                .labTab .search_accordion .card .card-header {
                  background-color: ${themeData.LabPrimaryColor || "#000000"}!important;
                  color: ${themeData.LabFontColor || "#ffffff"};
                }
                .labTab .search_btn_check > input:checked ~ .selectBox {
                    background-color:${themeData.LabPrimaryColor || "#000000"};
                    color: ${themeData.LabFontColor || "#ffffff"};
                }
                theme3 .labTab .search_btn_check > input:checked ~ .selectBox {
                  background-color:${themeData.LabPrimaryColor || "#000000"}!important;
                  border-radius:8px;
                }
                .labTab .selectBox:hover ,.theme3 .labTab .selectBox:hover{
                    border: 1px solid ${themeData.LabPrimaryColor || "#000000"}!important;
                }
                .labTab .search_btn_check > .selectBox {
                    background-color: ${themeData.LabPrimaryColor || "#000000"}10;
                    border: 1px solid ${themeData.LabPrimaryColor || "#000000"}40;
                }
                .theme3 .labTab .search_btn_check > .selectBox{
                  background-color: ${themeData.LabPrimaryColor || "#000000"}15;
                  border: 1px solid #00217300;
              }
                .labTab .search_btn_check > input:checked ~ .selectBox span {
                  color: ${themeData.LabFontColor || "#ffffff"};
                }

                .labTab .search_btn_check > input:checked ~ .selectBox g [fill] {
                  fill: ${themeData.LabFontColor || "#ffffff"};
                }
                .labTab .search_btn_check > input:checked ~ .selectBox svg {
                  fill: ${themeData.LabFontColor || "#ffffff"};
                }
                .labTab .text-primary{color: ${themeData.LabPrimaryColor || "#000000"}!important;}
                .labTab.text-primary{color: ${themeData.LabPrimaryColor || "#000000"}!important;}
                .labTab .btn.btn-primary {
                  color:${themeData.LabFontColor || "#ffffff"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"};
                  border-color: ${themeData.LabPrimaryColor || "#000000"};
                }

                .labTab .btn.btn-primary:hover:not(.btn-text):not(:disabled):not(.disabled),  .labTab .btn.btn-primary:focus:not(.btn-text),  .labTab .btn.btn-primary.focus:not(.btn-text) {
                  color:${themeData.LabFontColor || "#ffffff"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}cc;
                  border-color: ${themeData.LabPrimaryColor || "#000000"}cc;
                }

                .labTab .btn.btn-primary:not(:disabled):not(.disabled):active:not(.btn-text), .labTab .btn.btn-primary:not(:disabled):not(.disabled).active,  .labTab .show > .btn.btn-primary.dropdown-toggle, .labTab .show .btn.btn-primary.btn-dropdown {
                  color:${themeData.LabFontColor || "#ffffff"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}cc;
                  border-color: ${themeData.LabPrimaryColor || "#000000"}cc;
                }
                .labTab .btn.btn-light-primary {
                  color:${themeData.LabPrimaryColor || "#000000" || "#000000"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}10;
                  border-color: ${themeData.LabPrimaryColor || "#000000"}50;
                }

                .labTab .btn.btn-light-primary:hover:not(.btn-text):not(:disabled):not(.disabled),  .labTab .btn.btn-light-primary:focus:not(.btn-text),  .labTab .btn.btn-light-primary.focus:not(.btn-text) {
                  color:${themeData.LabPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}10;
                  border-color: ${themeData.LabPrimaryColor || "#000000"};
                }

                .labTab .btn.btn-light-primary:not(:disabled):not(.disabled):active:not(.btn-text), .labTab .btn.btn-light-primary:not(:disabled):not(.disabled).active,  .labTab .show > .btn.btn-light-primary.dropdown-toggle, .natTab .show .btn.btn-light-primary.btn-dropdown {
                 color:${themeData.LabPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}10;
                  border-color: ${themeData.LabPrimaryColor || "#000000"};
                }
                  .labTab .btn-text-primary{
                    color: ${themeData.LabPrimaryColor || "#000000"}!important;
                }
                .labTab .text-hover-primary{
                    color: ${themeData.LabPrimaryColor || "#000000"}!important;
                }
                    .labTab .btn-text-info{
                    color: ${themeData.LabPrimaryColor || "#000000"}50!important;
                }
                .labTab .btn.btn-hover-light-info:hover:not(.btn-text):not(:disabled):not(.disabled), .labTab .btn.btn-hover-light-info:focus:not(.btn-text), .labTab .btn.btn-hover-light-info.focus:not(.btn-text){
                  background-color: ${themeData.LabPrimaryColor || "#000000"}10;
                }
                .labTab .btn.btn-hover-light-primary:hover:not(.btn-text):not(:disabled):not(.disabled), .labTab .btn.btn-hover-light-primary:focus:not(.btn-text), .labTab .btn.btn-hover-light-primary.focus:not(.btn-text){
                  color:${themeData.LabPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}10;
                }
                
                .labTab .MuiSlider-colorSecondary {
                    color: ${themeData.LabPrimaryColor || "#000000"};
                }
                .labTab .MuiTabs-indicator{ background-color: ${themeData.LabPrimaryColor || "#000000"};}
                .labTab .custom-seprator{
                  border-top: 1px solid  ${themeData.LabPrimaryColor || "#000000"};
                }
                .labTab .custom-result-box{
                  color: ${themeData.LabPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.LabPrimaryColor || "#000000"}0a !important;
                  border-color: ${themeData.LabPrimaryColor || "#000000"} !important;
                }
                .labTab .MuiTab-textColorSecondary.Mui-selected{
                  color: ${themeData.LabPrimaryColor || "#000000"};
                }
                .labTab .MuiSlider-valueLabel span span {
                  color: ${themeData.LabFontColor || "#ffffff"};
                }
                .labTab .border-primary{
                  border-color:${themeData.LabPrimaryColor || "#000000"}!important;
                  color:${themeData.LabPrimaryColor || "#000000"}!important;
                }
                .labTab .bg-primary-o-20{
                  background-color:${themeData.LabPrimaryColor || "#000000"}33!important;
                }
                  .labTab a{
                     color:${themeData.LabPrimaryColor || "#000000"}!important;
                }
                .labTab a:hover{
                     color:${themeData.LabPrimaryColor || "#000000"}!important;
                }

                 .labTab .sorting .MuiOutlinedInput-inputMarginDense {
                 color:${themeData.LabPrimaryColor || "#000000"}!important;
                }
                 .labTab .sorting-menu{
                  color:${themeData.LabPrimaryColor || "#000000"}!important;
                 }
                   .labTab .sorting-menu:hover{
                  color:${themeData.LabPrimaryColor || "#000000"}!important;
                 }
                
               .labTab  .sorting .MuiOutlinedInput-notchedOutline {
                 border-color:${themeData.LabPrimaryColor || "#000000"}!important;
                  
                }
                .labTab .sorting .MuiSelect-icon{
                 color:${themeData.LabPrimaryColor || "#000000"}!important;
                }

                .labTab .btn-info{
                    color:${themeData.LabFontColor || "#ffffff"}!important;
                    background-color:${themeData.LabPrimaryColor || "#000000"}50!important;
                    border: none;
                    

                 }
                  .labTab .btn-info:hover{
                    background-color:${themeData.LabPrimaryColor || "#000000"}50!important;

                 }




                body{
                    ${themeData?.background_image
                      ? `background-image: url('${LOGO_BASE_URL}/${themeData.background_image}') !important;`
                      : ''}
                    background-color:${themeData.background_color||"#EEF0F8"};
                    transition: background-image 1s;
                     background-attachment: scroll !important; /* Allow image to scroll with the page */
                    background-size:cover!important;
                }

                .header {
                  background-color: ${themeData.nav_bar_color || "#ffffff"}!important;
                  color: ${themeData.nav_bar_font || "#000000" || "#000000"}!important;
                }
                .header .user-logo-alt-name {
                  color:  ${themeData.nav_bar_font || "#000000"|| "#000000"}!important;
                }
                    
                ::selection {
                    color: ${themeData.NaturalFontColor || "#ffffff"};
                    background: ${themeData.NaturalPrimaryColor || "#000000"};
                }
                body::-webkit-scrollbar {
                  height: 6px;
                  width:  6px;
                }
                /*Track */
                body::-webkit-scrollbar-track {
                    box-shadow: inset 0 0 2px grey;
                    background:#fff
                }
                /*Handle */
                body::-webkit-scrollbar-thumb {

                    background: ${themeData.NaturalPrimaryColor || "#000000"};
                    border-radius: 10px;
                }
                body::-webkit-scrollbar-thumb:hover {
                    background: ${themeData.NaturalPrimaryColor || "#000000"};
                    scrollbar-color: ${themeData.NaturalPrimaryColor || "#000000"}8f rgb(0 33 115 / 56%);
                }
                    
                .c_header.natTab .text-primary{
                  color: ${themeData.nav_bar_font || "#000000"}!important;
                }
                .c_header.natTab .MultiLingual .MuiOutlinedInput-inputMarginDense, .natTab .MuiSelect-icon{
                  color: ${themeData.nav_bar_font || "#000000"}!important;
                } 
                
                .c_header.natTab .symbol.symbol-light-primary .symbol-label{
                  background-color: ${themeData.nav_bar_font || "#000000"}20!important;
                  color:${themeData.nav_bar_font || "#000000"}!important;
                }
                .c_header.natTab .MultiLingual .MuiOutlinedInput-notchedOutline{
                  border-color: ${themeData.nav_bar_font || "#000000"}!important;
                }  

                .c-header-menu{
                  color: ${themeData.nav_bar_font || "#000000"}!important;
                } 
                  .c-header-menu:hover{
                  color: ${themeData.nav_bar_font || "#000000"}!important;
                } 
                  .c_header .navi .navi-item .navi-link:hover,.c_header .navi .navi-item .navi-link:hover .navi-text {
                  color: ${themeData.nav_bar_font || "#000000"}!important;
                  }
                   .c_header .navi .navi-item .navi-link,.c_header .navi .navi-item .navi-link .navi-text {
                  color: ${themeData.nav_bar_font || "#000000"}!important;
                  }

                .natTab .sorting .MuiOutlinedInput-inputMarginDense {
                 color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                }
                 .natTab .btn-info{
                    color:${themeData.NaturalFontColor || "#ffffff"}!important;
                    background-color:${themeData.NaturalPrimaryColor || "#000000"}50!important;
                    border: none;
                    

                 }
                  .natTab .btn-info:hover{
                    background-color:${themeData.NaturalPrimaryColor || "#000000"}50!important;

                 }
                 .sorting-menu,.sorting-menu:hover{
                  color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                 }
                
               .natTab  .sorting .MuiOutlinedInput-notchedOutline {
                 border-color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                  
                }
                .natTab .sorting .MuiSelect-icon{
                 color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                }
                 .natTab .text-hover-primary{
                    color: ${themeData.NaturalPrimaryColor || "#000000"}!important;
                }

                .natTab .border-primary{
                    border-color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                    color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                }
                .natTab .bg-primary-o-20{
                    background-color:${themeData.NaturalPrimaryColor || "#000000"}33!important;
                }
                .natTab .btn-text-primary{
                    color: ${themeData.NaturalPrimaryColor || "#000000"}!important;
                }
                .natTab .btn-text-info{
                    color: ${themeData.NaturalPrimaryColor || "#000000"}50!important;
                    

                }
                .natTab .btn.btn-hover-light-primary:hover:not(.btn-text):not(:disabled):not(.disabled),.natTab .btn.btn-hover-light-primary:focus:not(.btn-text),.natTab .btn.btn-hover-light-primary.focus:not(.btn-text){
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}10;
                }
                .natTab .btn.btn-hover-light-info:hover:not(.btn-text):not(:disabled):not(.disabled), .natTab .btn.btn-hover-light-info:focus:not(.btn-text), .natTab .btn.btn-hover-light-info.focus:not(.btn-text){
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}10;
                }
                
                .natTab a{
                    color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                }
                .natTab a:hover{
                    color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                }

                .table-responsive::-webkit-scrollbar {
                  height: 10px;
                  width:  10px;
                }
                /*Track */
                .table-responsive::-webkit-scrollbar-track {
                    box-shadow: inset 0 0 2px grey;
                }
                /*Handle */
                .table-responsive::-webkit-scrollbar-thumb {
                    background: ${themeData.NaturalPrimaryColor || "#000000"};
                    border-radius: 10px;
                }
                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: ${themeData.NaturalPrimaryColor || "#000000"}8f;
                    scrollbar-color: ${themeData.NaturalPrimaryColor || "#000000"}8f rgb(0 33 115 / 56%);
                }
                .natTab .search_accordion .card .card-header {
                    background-color: ${themeData.NaturalPrimaryColor || "#000000"}!important;
                    color: ${themeData.NaturalFontColor || "#ffffff"};
                }
                .natTab .search_btn_check > input:checked ~ .selectBox {
                    background-color:${themeData.NaturalPrimaryColor || "#000000"};
                    color: ${themeData.NaturalFontColor || "#ffffff"};
                }
                .theme3 .natTab .search_btn_check > .selectBox{
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}15;
                  border: 1px solid #00217300;
                }
                .natTab .selectBox:hover ,.theme3 .natTab .selectBox:hover{
                    border: 1px solid ${themeData.NaturalPrimaryColor || "#000000"}!important;
                }
                .natTab .search_btn_check > .selectBox {
                    background-color: ${themeData.NaturalPrimaryColor || "#000000"}10;
                    border: 1px solid ${themeData.NaturalPrimaryColor || "#000000"}40;
                }
                .natTab .search_btn_check > input:checked ~ .selectBox span {
                  color: ${themeData.NaturalFontColor || "#ffffff"};
                }
                .natTab .text-primary{color: ${themeData.NaturalPrimaryColor || "#000000"}!important;}
                .natTab.text-primary{color: ${themeData.NaturalPrimaryColor || "#000000"}!important;}
                .natTab .btn.btn-primary {
                  color:${themeData.NaturalFontColor || "#ffffff"}!important;
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"};
                  border-color: ${themeData.NaturalPrimaryColor || "#000000"};
                }

                .natTab .btn.btn-primary:hover:not(.btn-text):not(:disabled):not(.disabled),  .natTab .btn.btn-primary:focus:not(.btn-text),  .natTab .btn.btn-primary.focus:not(.btn-text) {
                  color:${themeData.NaturalFontColor || "#ffffff"}!important;
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}cc;
                  border-color: ${themeData.NaturalPrimaryColor || "#000000"}cc;
                }

                .natTab .btn.btn-primary:not(:disabled):not(.disabled):active:not(.btn-text), .natTab .btn.btn-primary:not(:disabled):not(.disabled).active,  .natTab .show > .btn.btn-primary.dropdown-toggle, .natTab .show .btn.btn-primary.btn-dropdown {
                  color:${themeData.NaturalFontColor || "#ffffff"}!important;
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}cc;
                  border-color: ${themeData.NaturalPrimaryColor || "#000000"}cc;
                }
                .natTab .btn.btn-light-primary {
                  color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}10;
                  border-color: ${themeData.NaturalPrimaryColor || "#000000"}50;
                }

                .natTab .btn.btn-light-primary:hover:not(.btn-text):not(:disabled):not(.disabled),  .natTab .btn.btn-light-primary:focus:not(.btn-text),  .natTab .btn.btn-light-primary.focus:not(.btn-text) {
                  color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}10;
                  border-color: ${themeData.NaturalPrimaryColor || "#000000"};
                }

                .natTab .btn.btn-light-primary:not(:disabled):not(.disabled):active:not(.btn-text), .natTab .btn.btn-light-primary:not(:disabled):not(.disabled).active,  .natTab .show > .btn.btn-light-primary.dropdown-toggle, .natTab .show .btn.btn-light-primary.btn-dropdown {
                 color:${themeData.NaturalPrimaryColor || "#000000"}!important;
                  background-color: ${themeData.NaturalPrimaryColor || "#000000"}10;
                  border-color: ${themeData.NaturalPrimaryColor || "#000000"};
                }
                .natTab .MuiSlider-colorSecondary {
                    color: ${themeData.NaturalPrimaryColor || "#000000"};
                }
                .natTab .MuiSlider-valueLabel span span {
                    color: ${themeData.NaturalFontColor || "#ffffff"};
                }
                .natTab .MuiTabs-indicator{ background-color: ${themeData.NaturalPrimaryColor || "#000000"};}
                .natTab .custom-seprator{
                  border-top: 1px solid  ${themeData.NaturalPrimaryColor || "#000000"};
                }
                .natTab .custom-result-box{
                    color: ${themeData.NaturalPrimaryColor || "#000000"}!important;
                    background-color: ${themeData.NaturalPrimaryColor || "#000000"}0a!important;
                    border-color: ${themeData.NaturalPrimaryColor || "#000000"} !important;
                }
                .natTab .MuiTab-textColorSecondary.Mui-selected{
                  color: ${themeData.NaturalPrimaryColor || "#000000"};
                }
                .natTab .search_btn_check > input:checked ~ .selectBox g [fill] {
                  fill: ${themeData.NaturalFontColor || "#ffffff"};
                }
                .natTab .search_btn_check > input:checked ~ .selectBox svg {
                  fill: ${themeData.NaturalFontColor || "#ffffff"};
                }

                .search_btn_check > input:checked ~ .selectBox g .round-svg[fill], .search_btn_check > input:checked ~ .selectBox g .cls-1round[fill], .search_btn_check > input:checked ~ .selectBox g .oval-shape[fill], .search_btn_check > input:checked ~ .selectBox g .cushion-shape1[fill]{
                  fill: none!important;
                }

                #kt_header_mobile {
                  background-color: ${themeData.nav_bar_color || "#ffffff"}!important;
                }

                     
            `}
            </style>
          }
            <Switch>
                {!isAuthorized ? (
                    /*Render auth page when user at `/auth` and not authorized.*/
                    <Route>
                        <AuthPage />
                    </Route>
                ) : (
                    /*Otherwise redirect to root page (`/`)*/
                    <Redirect from="/auth" to="/" />
                )}

                <Route path="/error" component={ErrorsPage} />
                <Route path="/logout" component={Logout} />

                {!isAuthorized ? (
                    /*Redirect to `/auth` when user is not authorized*/
                    <Redirect to="/" />
                ) : (
                    <SessionModalProvider language={selectedLanguage}>
                      <Layout language={selectedLanguage} themeData={themeData} translationsVersion={translationsVersion}>
                        <BasePage 
                          themeData={themeData} 
                          theme={theme} 
                          user={user} 
                          ruleData={ruleData} 
                          naturalData={naturalData} 
                          naturalFancyData={naturalFancyData} 
                          labData={labData} 
                          labFancyData={labFancyData} 
                          language={selectedLanguage}/>
                          {themeData?.disclaimer ? <Paper square data-disclaimer-strip ref={disclaimerRef} className={classes.disclaimerStrip} elevation={4}>
                            <Typography variant="subtitle1" component="p">
                              {themeData.disclaimer}
                            </Typography>
                          </Paper> : ""}
                      </Layout>
                    </SessionModalProvider>
                )}
            </Switch>
        </LanguageProvider>
    );
}
